export type Provider = "youtube" | "vimeo" | "generic";
export interface ParsedVideo {
  provider: Provider;
  videoId: string | null;
  url: string;
  startSeconds: number | null;
}

/** Client-side provider/ID parsing (spec §8.8 external video). */
export function parseVideoUrl(input: string): ParsedVideo | null {
  let u: URL;
  try {
    u = new URL(input.trim());
  } catch {
    return null;
  }
  if (!["http:", "https:"].includes(u.protocol)) return null;
  const host = u.hostname.replace(/^www\.|^m\./, "");
  const t = u.searchParams.get("t") ?? u.searchParams.get("start");
  const startSeconds = t ? parseTime(t) : null;
  if (host === "youtu.be") {
    const id = u.pathname.slice(1).split("/")[0] ?? "";
    return id ? { provider: "youtube", videoId: id, url: `https://www.youtube.com/watch?v=${id}`, startSeconds } : null;
  }
  if (host === "youtube.com" || host === "youtube-nocookie.com") {
    let id = u.searchParams.get("v");
    const m = u.pathname.match(/^\/(?:shorts|embed|live)\/([^/?]+)/);
    if (!id && m) id = m[1]!;
    return id ? { provider: "youtube", videoId: id, url: `https://www.youtube.com/watch?v=${id}`, startSeconds } : null;
  }
  if (host === "vimeo.com" || host === "player.vimeo.com") {
    const m = u.pathname.match(/(\d{5,})/);
    return m ? { provider: "vimeo", videoId: m[1]!, url: `https://vimeo.com/${m[1]}`, startSeconds } : null;
  }
  return { provider: "generic", videoId: null, url: u.toString(), startSeconds: null };
}

function parseTime(t: string): number | null {
  if (/^\d+$/.test(t)) return Number(t);
  const m = t.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
  if (!m) return null;
  return Number(m[1] ?? 0) * 3600 + Number(m[2] ?? 0) * 60 + Number(m[3] ?? 0);
}

export function embedUrl(v: { provider: Provider; videoId: string | null; startSeconds: number | null }): string | null {
  if (v.provider === "youtube" && v.videoId) {
    const s = v.startSeconds ? `?start=${v.startSeconds}` : "";
    return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(v.videoId)}${s}`;
  }
  if (v.provider === "vimeo" && v.videoId) return `https://player.vimeo.com/video/${encodeURIComponent(v.videoId)}`;
  return null;
}
